// @ts-strict-ignore
import { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Portfolio, RewardsProduct } from 'types/rewards'
import { getWithdrawValidationSchema } from '../components/Sheet/validationSchema'
import { RewardsWithdrawFormData, WithdrawSource } from '../types'

interface UseWithdrawFormStateProps {
  portfolioCurrency: string | null
  customerEarningsTotalUnpaid?: string
  userPortfolios: Portfolio[]
  rewardsProducts: RewardsProduct[]
}

interface UseWithdrawFormStateReturn {
  formMethods: UseFormReturn<RewardsWithdrawFormData>
  selectedCurrency: string
  selectedWallet: RewardsWithdrawFormData['withdrawToWallet']
  enteredAmount: string
  withdrawSource: WithdrawSource
  maxAmount: string
  setMaxAmount: (amount: string) => void
}

export const useWithdrawFormState = ({
  portfolioCurrency,
  customerEarningsTotalUnpaid,
  userPortfolios,
  rewardsProducts,
}: UseWithdrawFormStateProps): UseWithdrawFormStateReturn => {
  const { t } = useTranslation()
  const [maxAmount, setMaxAmount] = useState('')

  const formMethods = useForm<RewardsWithdrawFormData>({
    resolver: yupResolver(getWithdrawValidationSchema(t, maxAmount)),
    mode: 'onChange',
    defaultValues: {
      currency: portfolioCurrency || '',
      withdrawToWallet: null,
      amount: '',
      withdrawSource: 'myRewards',
    },
  })

  const { setValue, watch } = formMethods
  const selectedCurrency = watch('currency')
  const selectedWallet = watch('withdrawToWallet')
  const enteredAmount = watch('amount')
  const currentWithdrawSource = watch('withdrawSource')

  // Reset wallet and amount when currency changes
  useEffect(() => {
    if (selectedCurrency) {
      setValue('withdrawToWallet', null)
      setValue(
        'amount',
        currentWithdrawSource === 'customerRewards' ? customerEarningsTotalUnpaid || '' : ''
      )
    }
  }, [selectedCurrency, currentWithdrawSource, customerEarningsTotalUnpaid, setValue])

  // Set amount for customer rewards when source changes
  useEffect(() => {
    if (currentWithdrawSource === 'customerRewards' && customerEarningsTotalUnpaid) {
      setValue('amount', customerEarningsTotalUnpaid)
    }
  }, [currentWithdrawSource, customerEarningsTotalUnpaid, setValue])

  // Initialize max amount for single product scenario
  useEffect(() => {
    if (rewardsProducts.length === 1 && userPortfolios[0]) {
      setMaxAmount(userPortfolios[0].balance || '0')
    }
  }, [rewardsProducts, userPortfolios])

  return {
    formMethods,
    selectedCurrency,
    selectedWallet,
    enteredAmount,
    withdrawSource: currentWithdrawSource,
    maxAmount,
    setMaxAmount,
  }
}
